import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { Carousel } from '@one-thd/sui-carousel';
import { Typography } from '@one-thd/sui-atomic-components';
import { configureContainerForCarousel, configureContainerForWrap } from './utils/spotlightContainerConfig';

const SpotlightContainerRenderer = ({
  componentId = '',
  entryId = '',
  componentPosition = 1,
  title = '',
  overflowBehavior = 'Carousel',
  extraCardOnLargeAndAbove = false,
  cardCount = 0,
  spotlightComponents = [],
  fontWeight = 'display',
}) => {

  const spotlightContainerTitle = (
    <Typography
      variant="h2"
      weight={fontWeight}
      height="snug"
      color="primary"
      uppercase
      data-contentful-entry-id={componentId}
      data-contentful-field-id="title"
    >{title}
    </Typography>
  );

  const wrapContainer = (
    <div data-testid="spotlight-wrap-container" className="sui-bg-primary">
      {title && (
        <div data-component-id={componentId} className="sui-pb-4">
          {spotlightContainerTitle}
        </div>
      )}
      <div
        className={classNames(
          'sui-grid', 'sui-gap-4', 'md:sui-gap-6', 'xl:sui-gap-8',
          configureContainerForWrap(cardCount, extraCardOnLargeAndAbove))}
        data-contentful-entry-id={componentId}
        data-contentful-field-id="spotlightList"
      >
        {spotlightComponents}
      </div>
    </div>
  );

  const containerIdValue = componentId || `spotlight-container-${componentPosition}`;

  const carouselContainer = (
    <div data-testid="spotlight-carousel-container" className="sui-bg-primary">
      <div id={containerIdValue} data-testid="spotlight-carousel-wrapper">
        <Carousel
          containerId={containerIdValue}
          title={(
            <span
              data-contentful-entry-id={containerIdValue}
              data-contentful-field-id="title"
            >
              {spotlightContainerTitle}
            </span>
          )}
          breakpoints={configureContainerForCarousel(cardCount, extraCardOnLargeAndAbove)}
          slidesPerView={1}
          slidesPerGroup={1}
          spaceBetween={null}
          hidePaginationOnOneOfOne
          disableMargin
        >
          {spotlightComponents}
        </Carousel>
      </div>
    </div>
  );

  // If there are no cards, avoid rendering the Carousel (it may try to access DOM.style and crash).
  const shouldRenderCarousel = overflowBehavior === 'Carousel' && cardCount > 0;

  return (
    <ImpressionProvider
      key={componentPosition}
      data={{
        id: entryId || componentId,
        component: 'Section',
        name: 'Spotlight',
        type: 'content'
      }}
    >
      {shouldRenderCarousel ? carouselContainer : wrapContainer}
    </ImpressionProvider>
  );
};

SpotlightContainerRenderer.displayName = 'SpotlightContainerRenderer';
SpotlightContainerRenderer.propTypes = {
  componentId: PropTypes.string,
  entryId: PropTypes.string,
  componentPosition: PropTypes.number,
  title: PropTypes.string,
  overflowBehavior: PropTypes.string,
  extraCardOnLargeAndAbove: PropTypes.bool,
  cardCount: PropTypes.number,
  spotlightComponents: PropTypes.arrayOf(PropTypes.element),
  fontWeight: PropTypes.string,
};

export { SpotlightContainerRenderer };
